/* eslint-disable react/prop-types */
import React, { useContext, useState } from 'react';
import {
  Button,
  Card,
  Carousel,
  CarouselSlide,
  Column,
  GridContainer,
  H1,
  Icon,
  Link,
  Row,
  Spinner,
  Typography,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { useMutation } from 'react-query';
import { Preview } from '@rendering/product-preview-react';
import DesignFeedbackGroupRequestContext from '../../contexts/DesignFeedbackGroupRequestContext';
import { DesignFeedbackGroupResponseContext } from '../../contexts/DesignFeedbackGroupResponseContext';
import {
  submitCustomerFeedbackGroup,
  submitCustomerFeedbackGroupForAnonymousOrder,
} from '../../../apis/orderUpdateRequestsApi';
import useSiteData from '../../../hooks/useSiteData';
import { getShopperId } from '../../../util/auth';
import { RequestChangeModal } from './RequestChangeModal';
import { OriginalDesignModal } from './OriginalDesignModal';
import { FeedbackSubmissionStatus } from '../../../types/feedback/FeedbackResponse';
import { ErrorBanner } from './ErrorBanner';

const ReviewDesignsForGroup = () => {
  const [designFeedbackMessages, lineItemMessages, sharedActionMessages] =
    useTranslations([
      'designFeedbackMessages',
      'lineItemMessages',
      'sharedActionMessages',
    ]);

  const { tenantEnvironment, vistacartTenant } = useSiteData();
  const shopperId = getShopperId();

  const {
    orderPlacedAnonymously,
    groupId,
    orderId,
    emailAddress,
    proposedDesignOptions,
    previewLookupData,
    cimDoc,
  } = useContext(DesignFeedbackGroupRequestContext);

  // we always show the first option only
  const proposedDesignOption = proposedDesignOptions[0];

  const {
    approveDesignOption,
    rejectDesignOptions,
    requestedChanges,
    setIsFeedbackSubmitted,
    isFeedbackSubmitted,
  } = useContext(DesignFeedbackGroupResponseContext);

  const [isOriginalDesignModalOpen, setIsOriginalDesignModalOpen] =
    useState(false);
  const [isRequestChangeModalOpen, setIsRequestChangeModalOpen] =
    useState(false);

  /**
   * Mutations are used to create/update/delete data or perform server side-effects.
   * For more about mutations: https://tanstack.com/query/v4/docs/guides/mutations
   */
  const {
    mutate: submitFeedback,
    isLoading: isFeedbackSubmissionLoading,
    isError: isFeedbackSubmissionFailed,
  } = useMutation(
    async () => {
      const feedbackSubmission = {
        status: FeedbackSubmissionStatus.APPROVED,
        selectedOptionId: proposedDesignOption.optionId,
        customerRemarks: requestedChanges,
      };

      const trackingData = {
        groupId,
        orderNumber: orderId,
        selectedStatus: FeedbackSubmissionStatus.APPROVED,
        selectedOptionId: proposedDesignOption.optionId,
        requestedChanges,
        environment: tenantEnvironment,
        tenant: vistacartTenant,
      };

      if (orderPlacedAnonymously) {
        await submitCustomerFeedbackGroupForAnonymousOrder({
          orderNumber: orderId,
          groupId,
          email: emailAddress,
          feedbackSubmission,
          environment: tenantEnvironment,
          trackingData,
        });
      } else {
        await submitCustomerFeedbackGroup({
          orderNumber: orderId,
          groupId,
          shopperId,
          feedbackSubmission,
          environment: tenantEnvironment,
          trackingData,
        });
      }
    },
    {
      onSuccess: () => {
        setIsFeedbackSubmitted(true);
      },
    }
  );

  const disableApprovalButton =
    isFeedbackSubmissionLoading || isFeedbackSubmitted;

  return (
    <>
      <GridContainer>
        <Row>
          <Column span={12}>
            <Card bgc="strong">
              <Row>
                <Column span={12}>
                  <H1 fontSize="x2large" textAlign="center" mb={6}>
                    {designFeedbackMessages.reviewUpdatedDesign}
                  </H1>
                  <Card bordered border-width="10px" mb={6}>
                    <Row>
                      <Column span={12} mb={6}>
                        <Carousel
                          slidesToShow={1}
                          accessibleTextForPrevious={
                            lineItemMessages.previewProductAccessibleTextForPrevious
                          }
                          accessibleTextForNext={
                            lineItemMessages.previewProductAccessibleTextForNext
                          }
                        >
                          <CarouselSlide>
                            {/* user data is used for MCP team to track usage and possible error */}
                            {/* using the recommended data already */}
                            <Preview
                              user={{
                                tenant: {
                                  id: 'vistaprint-prod',
                                  type: 'merchants',
                                },
                                referer: 'ODUI',
                              }}
                              cimDocUrl={cimDoc}
                              view={previewLookupData.designPreview}
                            />
                          </CarouselSlide>
                          <CarouselSlide>
                            <Preview
                              user={{
                                tenant: {
                                  id: vistacartTenant,
                                  type: 'merchants',
                                },
                                referer: 'rendering-documentation',
                              }}
                              cimDocUrl={cimDoc}
                              view={previewLookupData.productPreview}
                            />
                          </CarouselSlide>
                        </Carousel>
                      </Column>
                    </Row>
                  </Card>
                  <Button
                    size="standard"
                    width="full-width"
                    skin="primary"
                    mb={3}
                    onClick={() => {
                      approveDesignOption(proposedDesignOption);
                      submitFeedback();
                    }}
                    disabled={disableApprovalButton}
                  >
                    <Typography>
                      {isFeedbackSubmissionLoading ? (
                        <Spinner
                          size="tiny"
                          accessibleText={sharedActionMessages.loading}
                        />
                      ) : (
                        <Icon iconType="guaranteedSatisfaction" mr={4} />
                      )}
                      {designFeedbackMessages.approve}
                    </Typography>
                  </Button>
                  {isFeedbackSubmissionFailed ? <ErrorBanner /> : null}
                  <Button
                    size="standard"
                    width="full-width"
                    skin="secondary"
                    onClick={() => {
                      rejectDesignOptions();
                      setIsRequestChangeModalOpen(true);
                    }}
                  >
                    <Typography>
                      <Icon iconType="edit" mr={4} />
                      {designFeedbackMessages.rejectAndRequestChanges}
                    </Typography>
                  </Button>
                </Column>
              </Row>
            </Card>
            <Row>
              <Column m="auto" mt={6}>
                <Link
                  render={props => (
                    <Button
                      skin="link"
                      width="full-width"
                      className={props.className}
                      onClick={() => {
                        setIsOriginalDesignModalOpen(true);
                      }}
                    >
                      {props.children}
                    </Button>
                  )}
                >
                  {designFeedbackMessages.viewOriginalDesign}
                </Link>
              </Column>
            </Row>
          </Column>
        </Row>
      </GridContainer>
      <RequestChangeModal
        isModalOpen={isRequestChangeModalOpen}
        setIsModalOpen={setIsRequestChangeModalOpen}
      />
      <OriginalDesignModal
        isModalOpen={isOriginalDesignModalOpen}
        setIsModalOpen={setIsOriginalDesignModalOpen}
      />
    </>
  );
};

export default ReviewDesignsForGroup;
